/**
 * Product gallery — catalogue images sourced from Firestore products_{tier}.
 * Images are managed in Product Management; this service maps rows to gallery entries.
 */

import { loadFirestorePricingRows } from './pricingTierService';
import {
    mergeCustomerDisplayCategoryOrder,
    resolveCustomerDisplayCategory,
} from '../utils/customerDisplayCategoryUtils';
import { normalizeImageUrlList } from '../utils/productImageSlotsContract';

const sanitize = (value) =>
    String(value || '')
        .replace(/\uFFFD/g, '')
        .replace(/[^\x00-\x7F]/g, '')
        .trim();

/** Standard embedded placeholder used when a product has no real image in Product Management. */
export const GALLERY_PLACEHOLDER_DATA_URL_PREFIX =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAOEAAADh';

/**
 * True when the URL is the standard placeholder (data URL or /placeholder-product.png).
 * @param {unknown} url
 */
export function isGalleryPlaceholderImage(url) {
    const value = String(url || '').trim();
    if (!value) return true;
    if (value === '/placeholder-product.png') return true;
    if (value === '/placeholder-product.svg') return true;
    if (value.startsWith('data:image/')) return true;
    if (value.startsWith(GALLERY_PLACEHOLDER_DATA_URL_PREFIX)) return true;
    return false;
}

/**
 * Remove placeholder / empty image URLs from a list.
 * @param {string[]} urls
 * @returns {string[]}
 */
export function filterGalleryImageUrls(urls = []) {
    const seen = new Set();
    const result = [];
    for (const raw of urls) {
        const url = String(raw || '').trim();
        if (!url || isGalleryPlaceholderImage(url) || seen.has(url)) continue;
        seen.add(url);
        result.push(url);
    }
    return result;
}

/**
 * Strip pack-size variant suffixes so 500g / 1kg SKUs share one gallery card.
 * @param {string} name
 * @param {string} [packSize]
 */
export function deriveBaseProductName(name, packSize = '') {
    let base = sanitize(name);
    if (!base) return '';

    base = base
        .replace(/\s*[-–—]\s*\d+\s*(kg|g|ml|l|pack|pcs|slc|cas|pce)\b.*$/i, '')
        .replace(/\s*[-–—]\s*\d+\s*x\s*\d+\s*(g|kg|ml|l)\b.*$/i, '')
        .trim();

    const pack = sanitize(packSize);
    if (pack) {
        const escaped = pack.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        base = base.replace(new RegExp(`\\s*[-–—]?\\s*${escaped}\\s*$`, 'i'), '').trim();
    }

    return base || sanitize(name);
}

/**
 * Extract real (non-placeholder) image URLs from a Firestore product row.
 * Prefers full ordered `ImageURLs` (deduped, max 6). Falls back to legacy
 * `Image URL` / `imageUrl` only when the slot list is empty after normalize.
 * @param {object} row
 * @returns {string[]}
 */
export function getProductImageUrls(row) {
    if (!row || typeof row !== 'object') return [];
    const fromSlots = normalizeImageUrlList(
        Array.isArray(row.ImageURLs) ? row.ImageURLs : []
    );
    const legacy = String(row['Image URL'] || row.imageUrl || '').trim();
    const urls = fromSlots.length
        ? fromSlots
        : legacy
          ? normalizeImageUrlList([legacy])
          : [];
    return filterGalleryImageUrls(urls);
}

/**
 * Build a stable gallery entry id from category + base product name.
 * @param {string} categoryLabel
 * @param {string} baseName
 */
export function galleryEntryId(categoryLabel, baseName) {
    const slug = `${categoryLabel}_${baseName}`
        .toLowerCase()
        .replace(/[^\w.-]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 96);
    return slug || 'product';
}

/**
 * Map one Firestore product row to a gallery row candidate (null when no real images).
 * @param {object} row
 * @returns {object|null}
 */
export function mapProductRowToGalleryEntry(row) {
    if (!row || typeof row !== 'object') return null;

    const imageUrls = getProductImageUrls(row);
    if (!imageUrls.length) return null;

    const code = String(row.Code || '').trim();
    const fullName = sanitize(row.Name || row.name || 'Unknown product');
    const packSize = sanitize(row['Pack Size'] || row.packSize || '');
    const name = deriveBaseProductName(fullName, packSize);
    const categoryLabel = resolveCustomerDisplayCategory({
        Name: row.Name || row.name,
        Code: code,
        Category: row.Category || row.category,
        packSize,
    });

    return {
        code,
        fullName,
        name,
        packSize,
        categoryLabel,
        imageUrls,
    };
}

/**
 * Merge pack-size variant rows into one gallery entry per base product + category.
 * @param {object[]} candidates
 * @returns {object[]}
 */
export function mergeGalleryEntriesByBaseProduct(candidates = []) {
    const groups = new Map();

    for (const candidate of candidates) {
        if (!candidate) continue;
        const groupKey = `${candidate.categoryLabel}||${candidate.name.toLowerCase()}`;
        const existing = groups.get(groupKey);

        if (!existing) {
            groups.set(groupKey, {
                id: galleryEntryId(candidate.categoryLabel, candidate.name),
                name: candidate.name,
                categoryLabel: candidate.categoryLabel,
                imageUrls: [...candidate.imageUrls],
                codes: candidate.code ? [candidate.code] : [],
            });
            continue;
        }

        if (candidate.code && !existing.codes.includes(candidate.code)) {
            existing.codes.push(candidate.code);
            existing.codes.sort((a, b) => a.localeCompare(b));
        }

        for (const url of candidate.imageUrls) {
            if (!existing.imageUrls.includes(url)) {
                existing.imageUrls.push(url);
            }
        }
    }

    return Array.from(groups.values()).map((entry) => ({
        ...entry,
        primaryImageUrl: entry.imageUrls[0] || null,
        code: entry.codes[0] || '',
    }));
}

/**
 * Build sorted gallery entries from raw Firestore product rows.
 * One entry per base product (pack-size variants merged).
 * @param {object[]} rows
 * @returns {{ entries: object[], categoryOrder: string[] }}
 */
export function buildGalleryEntriesFromRows(rows = []) {
    const candidates = [];

    for (const row of rows) {
        const entry = mapProductRowToGalleryEntry(row);
        if (entry) candidates.push(entry);
    }

    const entries = mergeGalleryEntriesByBaseProduct(candidates);

    entries.sort((a, b) => {
        const order = mergeCustomerDisplayCategoryOrder([
            a.categoryLabel,
            b.categoryLabel,
        ]);
        const aIndex = order.indexOf(a.categoryLabel);
        const bIndex = order.indexOf(b.categoryLabel);
        const ai = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const bi = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        if (ai !== bi) return ai - bi;
        return a.name.localeCompare(b.name);
    });

    const categoryOrder = mergeCustomerDisplayCategoryOrder(
        entries.map((entry) => entry.categoryLabel)
    );

    return { entries, categoryOrder };
}

/**
 * Load product gallery entries for a pricing tier.
 * @param {string} pricingKey
 * @returns {Promise<{ entries: object[], categoryOrder: string[], pricingKey: string, error: string|null }>}
 */
export async function loadProductGalleryEntries(pricingKey) {
    const { rows, pricingKey: key, error } = await loadFirestorePricingRows(pricingKey, {
        catalogVisibleOnly: true,
    });

    if (!rows.length) {
        return { entries: [], categoryOrder: [], pricingKey: key, error };
    }

    const { entries, categoryOrder } = buildGalleryEntriesFromRows(rows);
    return { entries, categoryOrder, pricingKey: key, error: null };
}

/**
 * Filter gallery entries by display category key ("all" shows everything).
 * @param {object[]} entries
 * @param {string} categoryKey
 */
export function filterGalleryEntriesByCategory(entries, categoryKey) {
    const key = String(categoryKey || 'all').trim();
    if (!key || key.toLowerCase() === 'all') return entries;
    return entries.filter((entry) => entry.categoryLabel === key);
}

/**
 * Suggest a download filename for a gallery image.
 * @param {object} entry
 * @param {number} [imageIndex]
 */
export function suggestGalleryDownloadFilename(entry, imageIndex = 0) {
    const slug = String(entry?.name || entry?.code || 'product')
        .trim()
        .replace(/[^\w.-]+/g, '_');
    const suffix = imageIndex > 0 ? `_${imageIndex + 1}` : '';
    const url = entry?.imageUrls?.[imageIndex] || entry?.primaryImageUrl || '';
    const extMatch = String(url).match(/\.(jpe?g|png|webp|gif)(\?|$)/i);
    const ext = extMatch ? extMatch[1].toLowerCase() : 'jpg';
    return `London-Smoke-and-Cure_${slug}${suffix}.${ext}`;
}
